import { Delaunay } from "d3-delaunay";

// Découpe un cadre en N morceaux qui le pavent entièrement, aux contours
// froissés comme du papier déchiré.
//
// Pourquoi un diagramme de Voronoï : poser des `clip-path` sur une grille
// laisserait le fond visible entre les tuiles — on obtiendrait des vignettes
// éparpillées, pas un mur. Un Voronoï, lui, pave le plan exactement.
//
// Les tuiles se touchent bord à bord, et la déchirure est tracée par-dessus :
// un trait unique par arête, d'épaisseur constante. Rentrer chaque cellule
// dans son coin pour ouvrir un interstice donnait une ligne qui s'épaissit et
// s'amincit au fil du bord — un pointillé plutôt qu'une déchirure.

// Espace de travail dans le rapport 16/9 du cadre, pour que les cellules
// restent à peu près isotropes avant conversion en pourcentages.
const W = 160, H = 90;
// Amplitude du froissement des bords, dans ce même espace.
const AMP = 1.7;

// Générateur reproductible : tous les joueurs doivent voir le même mur, donc
// la graine vient du code de la partie et non de Math.random.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

// Une arête est partagée par deux cellules : son froissement doit être calculé
// une seule fois et relu à l'identique des deux côtés, sinon les voisines ne
// coïncident plus et le pavage se troue.
function edgeKey(a, b) {
  const first = a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
  const [p, q] = first ? [a, b] : [b, a];
  return `${p[0].toFixed(3)},${p[1].toFixed(3)}|${q[0].toFixed(3)},${q[1].toFixed(3)}`;
}

// Points intermédiaires décalés perpendiculairement : c'est ce qui transforme
// un segment droit en bord arraché.
function tornEdge(a, b, cache, amp) {
  const key = edgeKey(a, b);
  let pts = cache.get(key);
  if (!pts) {
    const rnd = mulberry32(hash(key));
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len;          // normale au segment
    const steps = Math.max(3, Math.min(10, Math.round(len / 3)));
    pts = [];
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      // Le froissement s'éteint aux extrémités : l'arête repart exactement du
      // sommet du Voronoï, là où trois cellules se rejoignent.
      const off = (rnd() - 0.5) * 2 * amp * Math.sin(Math.PI * t);
      pts.push([a[0] + dx * t + nx * off, a[1] + dy * t + ny * off]);
    }
    cache.set(key, pts);
  }
  // Les deux cellules parcourent l'arête en sens inverse : on rend la liste
  // dans l'ordre du parcours demandé.
  const forward = a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
  return forward ? pts : [...pts].reverse();
}

// Centre de gravité d'un polygone, en pourcentages du cadre. Sur une figure
// dégénérée (aire nulle), la moyenne des sommets fait l'affaire.
function centroid(poly) {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i], [x2, y2] = poly[(i + 1) % poly.length];
    const cross = x1 * y2 - x2 * y1;
    a += cross; cx += (x1 + x2) * cross; cy += (y1 + y2) * cross;
  }
  if (Math.abs(a) < 1e-9) {
    cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
    cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  } else {
    cx /= 3 * a; cy /= 3 * a;
  }
  return [+((cx / W) * 100).toFixed(2), +((cy / H) * 100).toFixed(2)];
}

/**
 * @param {number} count  nombre de morceaux
 * @param {string} seed   même graine → même mur pour tout le monde
 * @returns {{shapes: string[], centers: number[][], boxes: number[][], tears: string}}
 *   `shapes`  : une valeur `clip-path: polygon(...)` par morceau.
 *   `centers` : le centre de chaque morceau, en pourcentages du cadre — les
 *               tuiles occupent toutes le cadre entier et ne se distinguent
 *               que par leur découpe, donc un titre posé à « 50 % 50 % »
 *               atterrirait au milieu du mur, pas au milieu du morceau.
 *   `boxes`   : le rectangle englobant de chaque morceau [x, y, l, h], en
 *               pourcentages du cadre — de quoi y caler l'image du fragment
 *               au lieu de l'étaler sur tout le mur.
 *   `tears`   : le tracé SVG de toutes les déchirures, à poser par-dessus le
 *               mur dans un viewBox `0 0 160 90`.
 */
export function buildTornWall(count, seed) {
  if (count <= 0) return { shapes: [], centers: [], boxes: [], tears: "" };
  const rnd = mulberry32(hash(String(seed)));
  let points = Array.from({ length: count }, () => [rnd() * W, rnd() * H]);

  // Une seule passe de Lloyd : elle suffit à éliminer les cellules minuscules
  // coincées entre deux grandes, impossibles à cliquer. Deux passes rendaient
  // le mur trop régulier — on veut des morceaux de tailles franchement
  // différentes, comme sur une vraie déchirure.
  for (let pass = 0; pass < 1; pass++) {
    const v = Delaunay.from(points).voronoi([0, 0, W, H]);
    points = points.map((p, i) => {
      const cell = v.cellPolygon(i);
      if (!cell) return p;
      let cx = 0, cy = 0;
      for (let k = 0; k < cell.length - 1; k++) { cx += cell[k][0]; cy += cell[k][1]; }
      const n = cell.length - 1;
      return n > 0 ? [cx / n, cy / n] : p;
    });
  }

  const voronoi = Delaunay.from(points).voronoi([0, 0, W, H]);
  const cache = new Map();
  const drawn = new Set();                        // une arête, un seul trait
  const shapes = [];
  const centers = [];
  const boxes = [];
  let tears = "";

  for (let i = 0; i < count; i++) {
    const cell = voronoi.cellPolygon(i);
    if (!cell) { shapes.push(null); centers.push([50, 50]); boxes.push([0, 0, 100, 100]); continue; }
    // cellPolygon referme le contour : le dernier point répète le premier.
    const ring = cell.slice(0, -1);
    const poly = [];
    for (let k = 0; k < ring.length; k++) {
      const a = ring[k], b = ring[(k + 1) % ring.length];
      const line = [a, ...tornEdge(a, b, cache, AMP)];
      poly.push(...line);
      const key = edgeKey(a, b);
      if (!drawn.has(key)) {
        drawn.add(key);
        tears += "M" + [...line, b].map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join("L");
      }
    }
    shapes.push("polygon(" + poly
      .map(([x, y]) => `${((x / W) * 100).toFixed(2)}% ${((y / H) * 100).toFixed(2)}%`)
      .join(", ") + ")");
    centers.push(centroid(poly));
    const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
    const x0 = Math.min(...xs), y0 = Math.min(...ys);
    boxes.push([(x0 / W) * 100, (y0 / H) * 100,
      ((Math.max(...xs) - x0) / W) * 100, ((Math.max(...ys) - y0) / H) * 100]);
  }
  return { shapes, centers, boxes, tears };
}
